import React, { useEffect, useState } from "react";
import { api, PLATFORM_LABELS, STATUS_CONFIG, STATUS_FLOW } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ExternalLink, Save, Camera, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";

function fmt(iso) {
  try {
    return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export function CaseDetail({ caseItem, onClose, onUpdated }) {
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("gefunden");
  const [localData, setLocalData] = useState(null);
  const [shotUrl, setShotUrl] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [reporting, setReporting] = useState(false);

  useEffect(() => {
    if (caseItem) {
      setLocalData(caseItem);
      setNotes(caseItem.notes || "");
      setStatus(caseItem.status);
    }
  }, [caseItem]);

  useEffect(() => {
    let revoked = null;
    const path = localData?.screenshot_path;
    if (!path) { setShotUrl(null); return; }
    (async () => {
      try {
        const r = await api.get(`/files/${path}`, { responseType: "blob" });
        const url = URL.createObjectURL(r.data);
        revoked = url;
        setShotUrl(url);
      } catch {
        setShotUrl(null);
      }
    })();
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [localData?.screenshot_path]);

  if (!caseItem) return null;
  const data = localData || caseItem;
  const setData = setLocalData;

  const captureScreenshot = async () => {
    setCapturing(true);
    try {
      const r = await api.post(`/cases/${data.id}/screenshot`);
      setData(r.data);
      toast.success("Beweis-Screenshot erstellt.");
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Screenshot fehlgeschlagen (Seite evtl. gesperrt).");
    } finally {
      setCapturing(false);
    }
  };

  const generateReport = async () => {
    setReporting(true);
    try {
      const r = await api.post(`/cases/${data.id}/report`);
      setData(r.data);
      toast.success("PDF-Dokumentation erstellt.");
      onUpdated?.();
      await openReport(r.data.report_path);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF-Erstellung fehlgeschlagen.");
    } finally {
      setReporting(false);
    }
  };

  const openReport = async (path) => {
    const p = path || data.report_path;
    if (!p) return;
    try {
      const r = await api.get(`/files/${p}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
    } catch {
      toast.error("PDF konnte nicht geöffnet werden.");
    }
  };

  const saveStatus = async (newStatus) => {
    const r = await api.patch(`/cases/${data.id}/status`, { status: newStatus });
    setData(r.data);
    setStatus(newStatus);
    toast.success(`Status: ${STATUS_CONFIG[newStatus].label}`);
    onUpdated?.();
  };

  const saveNotes = async () => {
    const r = await api.patch(`/cases/${data.id}/notes`, { notes });
    setData(r.data);
    toast.success("Notiz gespeichert.");
    onUpdated?.();
  };

  return (
    <Dialog open={!!caseItem} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="case-detail-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-[#0A0A0A] font-heading text-lg font-bold text-white">
              {data.profile_name?.charAt(0) || "?"}
            </div>
            <DialogTitle className="font-heading text-2xl font-bold tracking-tight">
              {data.profile_name}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2 text-sm text-[#3A3A3C]">
            <span className="rounded-sm bg-[#F4F1EC] px-3 py-1 text-xs font-semibold text-[#0A0A0A]">{PLATFORM_LABELS[data.platform]}</span>
            {data.handle && <span className="font-mono text-xs text-[#8B8A8E]">{data.handle}</span>}
            {data.profile_url && (
              <a
                href={data.profile_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="detail-open-link"
                className="inline-flex items-center gap-1 font-medium text-[#0A0A0A] hover:text-[#A6A4A9]"
              >
                Beitrag öffnen <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>

          {data.profile_url && (
            <a
              href={data.profile_url}
              target="_blank"
              rel="noopener noreferrer"
              className="-mt-2 block break-all text-xs font-medium text-[#0A0A0A] underline decoration-[#D8D3CC] underline-offset-2 hover:text-[#A6A4A9]"
            >
              {data.profile_url}
            </a>
          )}

          {data.bio && (
            <div className="rounded-md border border-[#E8E4DF] bg-[#FAF7F2] p-4 text-sm text-[#3A3A3C]">
              {data.bio}
            </div>
          )}

          <div className="rounded-md border border-[#E8E4DF] bg-[#F4F1EC] p-5">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#8B8A8E]">KI-Bewertung</p>
              <span className={`font-heading text-2xl font-bold tabular-nums ${data.ai_confidence >= 70 ? "text-rose-600" : "text-[#0A0A0A]"}`}>
                {data.ai_confidence}%
              </span>
            </div>
            <p className="mt-2 text-sm text-[#3A3A3C]">{data.ai_reason || "Keine Begründung verfügbar."}</p>
            <p className="mt-1.5 text-xs text-[#8B8A8E]">Keyword: {data.matched_keyword}</p>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#8B8A8E]">Beweis-Screenshot</p>
              {data.profile_url && (
                <Button
                  data-testid="capture-screenshot-button"
                  onClick={captureScreenshot}
                  disabled={capturing}
                  size="sm"
                  className="rounded-md border border-[#E0DCD6] bg-white text-[#0A0A0A] shadow-sm hover:bg-[#FAF7F2]"
                >
                  {capturing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Camera className="mr-2 h-4 w-4" />}
                  {capturing ? "Erstellt …" : data.screenshot_path ? "Aktualisieren" : "Screenshot erstellen"}
                </Button>
              )}
            </div>
            {data.screenshot_at && (
              <p className="mb-2 text-xs text-[#8B8A8E]">Erfasst am {fmt(data.screenshot_at)} (mit Zeitstempel im Bild)</p>
            )}
            {shotUrl ? (
              <a href={shotUrl} target="_blank" rel="noopener noreferrer" data-testid="screenshot-image-link">
                <img src={shotUrl} alt="Beweis-Screenshot" className="w-full rounded-md border border-[#E8E4DF]" />
              </a>
            ) : (
              <div className="rounded-md border border-dashed border-[#E0DCD6] bg-[#FAF7F2] p-4 text-center text-xs text-[#8B8A8E]">
                {capturing ? "Screenshot wird erstellt – bitte warten …" : "Noch kein Screenshot vorhanden."}
              </div>
            )}
          </div>

          <div className="rounded-md border border-[#E8E4DF] bg-[#0A0A0A] p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#A6A4A9]">Fall-Dokumentation (PDF)</p>
                <p className="mt-1 text-xs text-white/70">
                  Professionelles Beweisdokument mit Screenshot, KI-Bewertung & Zeitstempel – auf dem Server gespeichert.
                </p>
                {data.report_at && (
                  <p className="mt-1 text-xs text-white/50">Erstellt am {fmt(data.report_at)}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {data.report_path && (
                  <Button
                    data-testid="open-report-button"
                    onClick={() => openReport()}
                    size="sm"
                    variant="outline"
                    className="rounded-md border-white/20 bg-transparent text-white hover:bg-white/10"
                  >
                    <FileText className="mr-2 h-4 w-4" /> PDF öffnen
                  </Button>
                )}
                <Button
                  data-testid="generate-report-button"
                  onClick={generateReport}
                  disabled={reporting}
                  size="sm"
                  className="rounded-md bg-white text-[#0A0A0A] hover:bg-[#F4F1EC]"
                >
                  {reporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
                  {reporting ? "Erstellt …" : data.report_path ? "Neu erstellen" : "PDF erstellen"}
                </Button>
              </div>
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-[#8B8A8E]">Status ändern</p>
            <Select value={status} onValueChange={saveStatus}>
              <SelectTrigger data-testid="detail-status-select" className="rounded-md border-[#E0DCD6]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-md">
                {STATUS_FLOW.map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_CONFIG[s].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-[#8B8A8E]">Notizen</p>
            <Textarea
              data-testid="detail-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Interne Notizen zum Fall …"
              className="min-h-[80px] rounded-md border-[#E0DCD6] bg-[#FAF7F2] focus-visible:ring-[#0A0A0A]"
            />
            <Button
              data-testid="save-notes-button"
              onClick={saveNotes}
              size="sm"
              className="mt-2 rounded-md border border-[#E0DCD6] bg-white text-[#0A0A0A] shadow-sm hover:bg-[#FAF7F2]"
            >
              <Save className="mr-2 h-4 w-4" /> Notiz speichern
            </Button>
          </div>

          <div>
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-[#8B8A8E]">Verlauf</p>
            <div className="space-y-2">
              {(data.history || []).slice().reverse().map((h, i) => (
                <div key={i} className="flex items-start gap-3 rounded-md border border-[#EDE9E3] bg-white p-3">
                  <StatusBadge status={h.status} />
                  <div className="min-w-0 flex-1">
                    {h.note && <p className="text-sm text-[#3A3A3C]">{h.note}</p>}
                    <p className="text-xs text-[#8B8A8E]">{fmt(h.timestamp)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
